import { createApi } from '../http/http-common';
import {
  Artwork,
  ArtworkResult,
  VFileUploaderFile,
} from '@/components/w2pFileUploader';
import { AxiosResponse } from 'axios';
import {
  ChangeColorCommand,
  ChangeColorResult,
  RemoveColorCommand,
  ArtworkBackgroundResult,
} from '@/web-components/import-wizard/typings';
import {
  authHandler,
  invalidTokenHandler,
  validTokenHandler,
} from './interceptor-handlers/AuthHandler';
import {
  SetArtworkBackgroundCommand,
  DoRoundCroppingCommand,
  RoundCroppingResult,
} from '@/web-components/import-wizard/typings/colorManagement/colorManagement';
import {
  CheckImageSmallDetailsCommand,
  ProcessTextCommand,
  SmallDetailsResult,
} from '@/typings/SmallDetails';
import { PmsColor } from '@midocean/webapps-shared-domain';
import { ProcessTextResult } from '@/components/w2pFileUploader/typings/Artwork';

const imageProcessingAPI = createApi({
  BASE_URL: process.env.VUE_APP_IMAGE_PROCESSING_BASE_URL,
});

// Setup interceptors for the ImageProccesing API
imageProcessingAPI.interceptors.request.use(authHandler, function (error) {
  return Promise.reject(error);
});
imageProcessingAPI.interceptors.response.use(
  validTokenHandler,
  invalidTokenHandler
);

export default {
  upload(
    data: Artwork,
    file: VFileUploaderFile
  ): Promise<AxiosResponse<ArtworkResult>> {
    const formData = new FormData();
    formData.append('file', file.file);
    formData.append('json', JSON.stringify(data));
    return imageProcessingAPI.post(`artworks/processArtwork`, formData, {
      headers: {
        'Content-type': 'multipart/form-data',
      },
    });
  },

  textToImage(
    command: ProcessTextCommand
  ): Promise<AxiosResponse<ProcessTextResult>> {
    return imageProcessingAPI.post(`artworks/processText`, command);
  },

  roundCropping(
    pngFile: string | Blob,
    command: DoRoundCroppingCommand
  ): Promise<AxiosResponse<RoundCroppingResult>> {
    const formData = new FormData();
    formData.append('file', pngFile);
    formData.append('json', JSON.stringify(command));
    return imageProcessingAPI.post('artworks/roundCropping', formData);
  },

  changeLogoBackground(
    pngFile: string | Blob,
    command: SetArtworkBackgroundCommand
  ): Promise<AxiosResponse<ArtworkBackgroundResult>> {
    const formData = new FormData();
    formData.append('file', pngFile);
    formData.append('json', JSON.stringify(command));
    return imageProcessingAPI.post('artworks/setBackground', formData);
  },
  getAvailablePmsColors(
    technique?: string
  ): Promise<AxiosResponse<PmsColor[]>> {
    if (technique) {
      return imageProcessingAPI.get(
        `availablePmsColors?techniqueCode=` + technique
      );
    } else {
      return imageProcessingAPI.get(`availablePmsColors`);
    }
  },

  replaceColor(
    command: ChangeColorCommand,
    files: Blob[]
  ): Promise<AxiosResponse<ChangeColorResult>> {
    const formData = new FormData();
    files.forEach((file, index) => {
      formData.append('files', file, `file-${index + 1}`);
    });
    formData.append('command', JSON.stringify(command));
    return imageProcessingAPI.post(`artworks/ChangeColor`, formData, {
      headers: {
        'Content-type': 'multipart/form-data',
      },
    });
  },

  detectColors(file: Blob): Promise<AxiosResponse<PmsColor[]>> {
    const formData = new FormData();

    formData.append('file', file, 'cropped-image.png');

    return imageProcessingAPI.post(`artworks/DetectColors`, formData, {
      headers: {
        'Content-type': 'multipart/form-data',
      },
    });
  },
  async removeColor(
    command: RemoveColorCommand,
    files: Blob[]
  ): Promise<AxiosResponse<ChangeColorResult>> {
    const formData = new FormData();
    files.forEach((file, index) => {
      formData.append('files', file, `file-${index + 1}`);
    });
    formData.append('command', JSON.stringify(command));
    const result = await imageProcessingAPI.post(
      `artworks/RemoveColor`,
      formData,
      {
        headers: {
          'Content-type': 'multipart/form-data',
        },
      }
    );
    return result;
  },

  applyLaserEffect(pngFile: Blob | string, config: string): Promise<any> {
    const formData = new FormData();
    formData.append('file', pngFile || '');
    formData.append('command', config);

    const result: Promise<any> = imageProcessingAPI.post(
      `artworks/SetLaserEffect`,
      formData,
      {
        headers: {
          'Content-type': 'multipart/form-data',
        },
      }
    );
    return result;
  },

  applyDebossingEffect(pngFile: Blob | string, command: string): Promise<any> {
    const formData = new FormData();
    formData.append('file', pngFile || '');
    formData.append('command', command);

    const result: Promise<any> = imageProcessingAPI.post(
      `artworks/SetDebossingEffect`,
      formData,
      {
        headers: {
          'Content-type': 'multipart/form-data',
        },
      }
    );
    return result;
  },

  checkImageSmallDetails(
    command: CheckImageSmallDetailsCommand,
    file: Blob
  ): Promise<AxiosResponse<SmallDetailsResult>> {
    const formData = new FormData();
    formData.append('json', JSON.stringify(command));
    formData.append('file', file);

    return imageProcessingAPI.post(`smalldetails`, formData, {
      headers: {
        'Content-type': 'multipart/form-data',
      },
    });
  },
};
